'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ArrowRight, Check, UserSearch } from 'lucide-react';
import { cn, getEvColorClass, useMediaQuery } from '@sharpr/ui';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { TeamLogo } from '@/components/TeamLogo';
import { BookThumbnail } from '@/components/BookThumbnail';
import { AnimatedSection } from '@/components/client/landing/AnimatedSection';
import {
  LineMovementOverlay,
  PlayerResearchOverlay,
  prefetchLineOverlay,
  prefetchResearchOverlay,
} from './overlays';
import { GLASS_CARD, SectionEyebrow, SectionTitle } from './shared';
import {
  DEMO_MOVEMENTS,
  DEMO_ROWS,
  EXAMPLE,
  LINE_TARGET,
  PMF,
  RESEARCH_TARGET,
  lineLabel,
  resultLabel,
} from './demoFeatured';
import { useDemoRowEntrance } from './useDemoRowEntrance';
import { captureEvent, EVENTS } from '@/lib/posthog/events';

// Desktop-only product demo (DemoProductView). Code-split with a same-height
// shell to avoid layout shift; only requested at lg+ via the `isDesktop` gate.
const DemoProductView = dynamic(
  () => import('./DemoProductView').then((m) => ({ default: m.DemoProductView })),
  {
    ssr: false,
    loading: () => <div aria-hidden="true" className={cn(GLASS_CARD, 'h-[588px]')} />,
  },
);

// --- Founder bio card -----------------------------------------------------------

// Public track record only, handle only (SBK, never a real name).
function FounderCard({ className }: { className?: string }) {
  return (
    <div className={cn(GLASS_CARD, className)}>
      <div className="px-4 sm:px-5 py-4 sm:py-5 space-y-4">
        <div className="flex items-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/sbk-astronaut.webp"
            alt="SBK's astronaut profile picture"
            className="h-12 w-12 rounded-full border border-[var(--glass-border)] bg-fg/5"
          />
          <p className="text-lg leading-tight">
            <span className="text-fg/60">Built by</span>{' '}
            <span className="font-semibold text-fg">SBK</span>
          </p>
        </div>

        <p className="text-sm font-medium text-fg/70 leading-relaxed">
          More than a decade building MLB and NBA player projections, first at
          RotoGrinders, most recently at Unabated Sports. Sharpr is what came
          next.
        </p>
      </div>
    </div>
  );
}

// --- Interactive demo card ------------------------------------------------------

/** Graded outcome chip — the edge as served, then what actually happened. */
function ResultChip({ label }: { label: string | null }) {
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-px text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
      <Check className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function DemoCard() {
  // Staggered row fade-in on scroll-in (globals.css `demo-row-in`).
  const { ref: entranceRef, entranceClass } = useDemoRowEntrance<HTMLDivElement>();

  // Latches keep chunks mounted so vaul/framer exit animations survive close.
  const [researchOpen, setResearchOpen] = useState(false);
  const [researchMounted, setResearchMounted] = useState(false);
  const [lineOpen, setLineOpen] = useState(false);
  const [lineMounted, setLineMounted] = useState(false);

  const openResearchOverlay = () => {
    setResearchMounted(true);
    setResearchOpen(true);
  };

  const openLineOverlay = () => {
    setLineMounted(true);
    setLineOpen(true);
  };

  return (
    <div ref={entranceRef} className={cn(GLASS_CARD, entranceClass)}>
      {/* Board header. */}
      <div className="flex items-baseline justify-between gap-3 px-4 sm:px-5 py-2.5 border-b border-[var(--glass-border)]">
        <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-fg/60 whitespace-nowrap">
          Demo slate
        </p>
        <p className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase text-brand-500 opacity-80 whitespace-nowrap">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Interactive
        </p>
      </div>

      {/* Live row: player → research card, line → projection tools. */}
      <div className="demo-fade-row relative flex items-center gap-3 px-3 sm:px-5 py-3.5 bg-[color-mix(in_srgb,var(--brand-500)_4%,transparent)]">
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-blue-400 via-blue-500 to-blue-600"
        />
        {/* flex-1 wrapper keeps the line chip pushed right. */}
        <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={openResearchOverlay}
          onMouseEnter={prefetchResearchOverlay}
          className={cn(
            'group flex max-w-full items-center gap-2.5 min-w-0 text-left rounded-lg px-2 sm:px-2.5 py-1.5 transition-colors',
            'border border-[color-mix(in_srgb,var(--brand-500)_35%,transparent)]',
            'bg-[color-mix(in_srgb,var(--brand-500)_8%,transparent)]',
            'hover:bg-[color-mix(in_srgb,var(--brand-500)_14%,transparent)]',
          )}
        >
          <PlayerAvatar name={EXAMPLE.player} imageUrl={EXAMPLE.imageUrl} size="md" />
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-brand-500 min-w-0">
              <span className="truncate">{EXAMPLE.player}</span>
              <UserSearch className="hidden sm:block h-3.5 w-3.5 shrink-0 opacity-70" />
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-fg/65 min-w-0">
              <TeamLogo team={EXAMPLE.team} sport="nba" className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{EXAMPLE.matchup}</span>
            </p>
          </div>
        </button>
        </div>

        <button
          type="button"
          onClick={openLineOverlay}
          onMouseEnter={prefetchLineOverlay}
          className={cn(
            'group relative flex shrink-0 items-center gap-1 sm:gap-2 rounded-lg px-2 sm:px-2.5 py-1.5 text-right transition-colors',
            'border border-[color-mix(in_srgb,var(--brand-500)_35%,transparent)]',
            'bg-[color-mix(in_srgb,var(--brand-500)_8%,transparent)]',
            'hover:bg-[color-mix(in_srgb,var(--brand-500)_14%,transparent)]',
          )}
        >
          <div>
            <div className="flex items-center justify-end gap-1.5">
              <span className={cn('text-sm font-semibold', getEvColorClass(EXAMPLE.ev))}>
                {EXAMPLE.evLabel}
              </span>
              <span className="uppercase text-xs text-fg/65">{EXAMPLE.marketLabel}</span>
              <BookThumbnail book={EXAMPLE.bestBook} size="sm" />
            </div>
            <div className="mt-0.5 font-mono text-xs text-fg/80">{lineLabel(EXAMPLE)}</div>
            <div className="mt-1 flex justify-end">
              <ResultChip label={resultLabel(EXAMPLE)} />
            </div>
          </div>
        </button>
      </div>

      {/* Dimmed rows — non-interactive teasers so the card reads as a slate. */}
      <div aria-hidden="true" className="border-t border-[var(--glass-border)] divide-y divide-[var(--glass-border)] opacity-50 select-none">
        {DEMO_ROWS.map((row, i) => (
          <div
            key={row.key}
            className="demo-fade-row flex items-center gap-3 px-3 sm:px-5 py-3"
            style={{ animationDelay: `${(i + 1) * 120}ms` }}
          >
            <PlayerAvatar name={row.player} imageUrl={row.imageUrl} size="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg truncate">{row.player}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-fg/65 min-w-0">
                <TeamLogo team={row.team} sport="nba" className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{row.matchup}</span>
              </p>
            </div>
            <div className="shrink-0 text-right">
              <div className="flex items-center justify-end gap-1.5">
                <span className={cn('text-sm font-semibold', getEvColorClass(row.ev))}>
                  {row.evLabel}
                </span>
                <span className="uppercase text-xs text-fg/65">{row.marketLabel}</span>
                <BookThumbnail book={row.bestBook} size="sm" />
              </div>
              <div className="mt-0.5 font-mono text-xs text-fg/80">{lineLabel(row)}</div>
              <div className="mt-1 flex justify-end">
                <ResultChip label={resultLabel(row)} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer — the real slate is public; send the curious straight to it. */}
      <Link
        href="/nba"
        onClick={() => captureEvent(EVENTS.CTA_CLICKED, { location: 'demo_projections_slate', label: 'See today\'s full slate', destination: '/nba' })}
        className="flex items-center justify-center gap-1.5 border-t border-[var(--glass-border)] py-2.5 text-xs font-semibold text-brand-500 hover:bg-fg/[0.03] transition-colors"
      >
        See today&apos;s full slate
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>

      {/* The actual product research card, live data via public endpoints. */}
      {researchMounted && (
        <PlayerResearchOverlay
          target={researchOpen ? RESEARCH_TARGET : null}
          onClose={() => setResearchOpen(false)}
          sport="nba"
        />
      )}

      {/* The actual line-click overlay: line movement, prop history, and the
          working EV calculator (fed the model's PMF for this prop). */}
      {lineMounted && (
        <LineMovementOverlay
          target={lineOpen ? LINE_TARGET : null}
          onClose={() => setLineOpen(false)}
          sport="nba"
          demoMovements={DEMO_MOVEMENTS}
          demoPmf={PMF}
          onOpenResearch={openResearchOverlay}
        />
      )}
    </div>
  );
}

// --- Section ------------------------------------------------------------------

export function ProjectionsShowcase() {
  // Mount gate keeps the heavy demo chunk off phones; CSS-hiding alone would
  // still download and hydrate it.
  const isDesktop = useMediaQuery('(min-width: 1024px)', false);

  return (
    // AnimatedSection so the section shares the page-wide fade cadence.
    <AnimatedSection id="projections-showcase" className="py-10 md:py-20 scroll-mt-16 md:scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Two-rail top on desktop: claim left, FounderCard right; mobile is copy-only. */}
        <div className="grid lg:grid-cols-[3fr_2fr] gap-10 lg:gap-14 items-center">
          <div className="max-w-2xl space-y-4">
            <SectionEyebrow>Projections</SectionEyebrow>
            <SectionTitle>
              The <span className="text-brand-500">true price</span> of every
              line.
            </SectionTitle>
            {/* Decade-of-projections credential: in FounderCard on desktop, in copy on mobile. */}
            <p className="text-base font-medium text-fg/70 leading-relaxed">
              Our model and team price every prop as a full outcome
              distribution, then sweep sportsbooks and exchanges for odds
              that beat it.
              <span className="lg:hidden">
                {' '}
                We&apos;ve built MLB and NBA projections for over a decade.
              </span>{' '}
              The slate below is a real example. Click around, it&apos;s the
              actual product.
            </p>
          </div>

          <FounderCard className="hidden lg:block" />
        </div>

        {/* Compact slate card — mobile/tablet only; lg+ gets the real demo below. */}
        <div className="mt-8 lg:hidden">
          <DemoCard />
        </div>

        {/* Desktop product demo. Visibility's one source of truth: the `isDesktop` gate. */}
        {isDesktop && (
          <div className="mt-10">
            <DemoProductView />
          </div>
        )}
      </div>
    </AnimatedSection>
  );
}
